import api from "services/api";

const authHeaders = () => {
  const token = localStorage.getItem("tk446655440000");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const value = (object, camel, pascal, fallback) =>
  object?.[camel] ?? object?.[pascal] ?? fallback;

export function normalizeEstablishment(row) {
  return {
    id: value(row, "id", "Id", null),
    name: value(row, "name", "Name", ""),
    code: value(row, "code", "Code", ""),
    type: value(row, "type", "Type", ""),
    status: value(row, "status", "Status", ""),
    creationDate: value(row, "creationDate", "CreationDate", ""),
    latitude: value(row, "latitude", "Latitude", ""),
    longitude: value(row, "longitude", "Longitude", ""),
    spId: value(row, "subPrefectureId", "SubPrefectureId", value(row, "spId", "SPId", "")),
    spName: value(row, "subPrefectureName", "SubPrefectureName", value(row, "spName", "SPName", "")),
    departmentId: value(row, "departmentId", "DepartmentId", ""),
    departmentName: value(row, "departmentName", "DepartmentName", ""),
    provinceId: value(row, "provinceId", "ProvinceId", ""),
    provinceName: value(row, "provinceName", "ProvinceName", ""),
    assignmentCount: Number(value(row, "assignmentCount", "AssignmentCount", 0)),
    attendanceCount: Number(value(row, "attendanceCount", "AttendanceCount", 0)),
    isActive: Boolean(value(row, "isActive", "IsActive", true)),
    createdAt: value(row, "createdAt", "CreatedAt", ""),
    updatedAt: value(row, "updatedAt", "UpdatedAt", ""),
    rowVersion: value(row, "rowVersion", "RowVersion", ""),
  };
}

export async function getEstablishments({
  pageNumber = 1,
  pageSize = 25,
  search = "",
  provinceId = "",
  departmentId = "",
  subPrefectureId = "",
} = {}) {
  const { data } = await api.get("/web/establishments", {
    headers: authHeaders(),
    params: {
      pageNumber,
      pageSize,
      search: search || undefined,
      provinceId: provinceId || undefined,
      departmentId: departmentId || undefined,
      subPrefectureId: subPrefectureId || undefined,
      sortBy: "name",
      sortDirection: "asc",
    },
  });
  const items = value(data, "items", "Items", []);
  const pagination = value(data, "pagination", "Pagination", {});
  const totalRecords = Number(value(pagination, "totalRecords", "TotalRecords", items?.length || 0));
  return {
    items: (Array.isArray(items) ? items : []).map(normalizeEstablishment),
    pagination: {
      pageNumber: Number(value(pagination, "pageNumber", "PageNumber", pageNumber)),
      pageSize: Number(value(pagination, "pageSize", "PageSize", pageSize)),
      totalRecords,
      totalPages: Number(
        value(pagination, "totalPages", "TotalPages", Math.max(1, Math.ceil(totalRecords / pageSize)))
      ),
    },
  };
}

export async function getAllEstablishments(filters = {}) {
  const pageSize = 100;
  const firstPage = await getEstablishments({ ...filters, pageNumber: 1, pageSize });
  const totalPages = firstPage.pagination.totalPages;
  if (totalPages <= 1) return firstPage.items;

  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getEstablishments({ ...filters, pageNumber: index + 2, pageSize })
    )
  );
  return [firstPage, ...remainingPages].flatMap((page) => page.items);
}

export async function getEstablishment(id) {
  const { data } = await api.get(`/web/establishments/${id}`, { headers: authHeaders() });
  return normalizeEstablishment(data);
}

export async function createEstablishment(payload) {
  const { data } = await api.post("/web/establishments", payload, { headers: authHeaders() });
  return normalizeEstablishment(data);
}

export async function updateEstablishment(id, payload) {
  const { data } = await api.put(`/web/establishments/${id}`, payload, { headers: authHeaders() });
  return normalizeEstablishment(data);
}

export async function getEstablishmentDeletionImpact(id) {
  const { data } = await api.get(`/web/establishments/${id}/deletion-impact`, {
    headers: authHeaders(),
  });
  return data;
}

export async function archiveEstablishment(id, payload) {
  const { data } = await api.delete(`/web/establishments/${id}`, {
    headers: authHeaders(),
    data: payload,
  });
  return data;
}

function normalizeLookup(data) {
  const items = value(data, "items", "Items", Array.isArray(data) ? data : []);
  return (Array.isArray(items) ? items : []).map((item) => ({
    id: value(item, "id", "Id", null),
    text: value(item, "name", "Name", ""),
  }));
}

export async function getWebProvinces() {
  const { data } = await api.get("/web/geography/provinces", { headers: authHeaders() });
  return normalizeLookup(data);
}

export async function getWebDepartments(provinceId) {
  const { data } = await api.get(`/web/geography/provinces/${provinceId}/departments`, {
    headers: authHeaders(),
  });
  return normalizeLookup(data);
}

export async function getWebSubPrefectures(departmentId) {
  const { data } = await api.get(`/web/geography/departments/${departmentId}/sub-prefectures`, {
    headers: authHeaders(),
  });
  return normalizeLookup(data);
}

export async function importEstablishments(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/web/establishments/import", formData, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
  });
  return data;
}
